import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { NgScrollbar } from 'ngx-scrollbar';
import { FormsModule, NgForm } from '@angular/forms';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AsyncPipe, NgClass } from '@angular/common';
import { FloatingInputComponent } from '../../../../inputs/floating-input/floating-input.component';
import { InputCalendarComponent } from '../../../../inputs/input-calendar/input-calendar.component';
import { DropdownComponent } from '../../../../inputs/dropdown/dropdown.component';
import { SimpleButtonComponent } from '../../../../buttons/simple-medium-button/simple-button.component';
import { SidebarHeaderComponent } from '../../../sidebar-header/sidebar-header.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import {
  CHANGE_ACTIONS_TYPE,
  changeOrderRequestStatuses,
} from '@app/store/prime-commitments/prime-commitments.constants';
import { COR_STATUS } from '@app/store/prime-commitments/prime-commitments.types';
import { primeSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { NotificationsService } from '@app/services/notifications.service';
import { primeChangesSidebarActions } from '@app/store/prime-commitments/prime-commitments.actions';
import { ChangeActionsStore } from '@app/store/change-actions/change-actions.store';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { LoadingBarComponent } from '@app/framework/inputs/loading-bar/loading-bar.component';
import { isPersistedFile, ManagedFile } from '@app/framework/interfaces/file.type';
import { pcoActions } from '@app/store/pco/pco.actions';
import { pcoSelectors } from '@app/store/pco/pco.selectors';
import { POTENTIAL_CHANGE_ORDER_STATUSES } from '@app/store/pco/pco.constants';
import { PrimeChangesGeneral, PCO_STATUS } from '@app/store/pco/pco.types';
import { InputUploadComponent } from '@app/framework/file-handling/input-upload/input-upload.component';

@Component({
  selector: 'app-change-action-general-sidebar',
  imports: [
    NgScrollbar,
    FormsModule,
    FloatingInputComponent,
    InputCalendarComponent,
    DropdownComponent,
    SimpleButtonComponent,
    CdkTextareaAutosize,
    SidebarHeaderComponent,
    TooltipDirective,
    AsyncPipe,
    NgClass,
    LoadingBarComponent,
    InputUploadComponent,
  ],
  templateUrl: './change-action-general-sidebar.component.html',
  styleUrl: './change-action-general-sidebar.component.scss',
})
export class ChangeActionGeneralSidebarComponent implements AfterViewInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly notif = inject(NotificationsService);
  private readonly changeActionsStore = inject(ChangeActionsStore);

  @ViewChild('generalForm') generalForm: NgForm;

  @Input() model: PrimeChangesGeneral;
  @Output() modelChange = new EventEmitter<PrimeChangesGeneral>();
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() submitPcoUpdate = new EventEmitter<void>();

  originalNumber = null;

  statuses: (PCO_STATUS | COR_STATUS)[] = changeOrderRequestStatuses;
  protected readonly CHANGE_ACTIONS_TYPE = CHANGE_ACTIONS_TYPE;

  changeActionTypes = [
    CHANGE_ACTIONS_TYPE.POTENTIAL_CHANGE_ORDER,
    CHANGE_ACTIONS_TYPE.CHANGE_ORDER_REQUEST,
  ];
  customIdDisabled = true;
  currentPage: 1 | 2 = 1;
  possibleDurationExtensions: string[] = Array(100)
    .fill(0, 0, 100)
    .map((_, i) => String(i));

  isPCOValid$ = this.store.select(pcoSelectors.selectIsPCONumberValid);
  isCORValid$ = this.store.select(primeSelectors.selectIsCORNumberValid);
  nextPCONumber$ = this.store.select(pcoSelectors.selectNextPCONumber);
  nextCORNumber$ = this.store.select(primeSelectors.selectNextCORNumber);
  PCOEditableFields$ = this.store.select(pcoSelectors.selectPCOSidebarEditableFields);
  project$ = this.store.select(viewProjectSelectors.getSelectedProject);

  allPCOs = this.changeActionsStore.pcos;
  isLoadingPCOs = this.changeActionsStore.pcosLoading;
  isDestroyed$ = new Subject<void>();

  ngAfterViewInit() {
    this.validateNumber();
    if (this.model.status) {
      this.setStatusOptions();
    }

    if (this.model.number) {
      this.originalNumber = this.model.number;
    }
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  onNext() {
    this.generalForm.form.markAllAsTouched();

    // todo: find out wht number is not found
    console.log('General form errors:', this.generalForm.form.controls);
    if (this.generalForm.form.controls.number.errors?.pattern) {
      this.notif.showError('Number is already taken');
    }

    if (this.generalForm.form.invalid) {
      return;
    }

    this.next.emit();
  }

  onCustomIdIconClick() {
    if (!this.customIdDisabled) {
      this.revertToOriginalNumber();
    }

    this.customIdDisabled = !this.customIdDisabled;
  }

  onBack() {
    this.back.emit();
  }

  revertToOriginalNumber() {
    this.model.number = this.originalNumber;
  }

  setNextNumber() {
    if (this.model.type === 'Potential Change Order') {
      this.setPCONumber();
      return;
    }
    this.setNextCORNumber();
  }

  private setNextCORNumber() {
    this.nextCORNumber$
      .pipe(
        filter((data) => data !== null),
        take(1),
      )
      .subscribe((nextNumber) => {
        this.model.number = nextNumber;
      });
  }

  private setPCONumber() {
    this.nextPCONumber$
      .pipe(
        filter((data) => data !== null),
        take(1),
      )
      .subscribe((nextPCONumber) => {
        this.model.number = nextPCONumber;
      });
  }

  /**
   * Validates the PCO number in the store if it's unique
   * @param number
   * @constructor
   */
  NumberChanged(number: string) {
    if (this.model.type === 'Change Order Request') {
      this.store.dispatch(
        primeChangesSidebarActions.onCORNumberChange({ number, resource_id: this.model.id }),
      );
      return;
    }
    this.store.dispatch(pcoActions.onPCONumberChange({ number, resource_id: this.model.id }));
  }

  validateNumber() {
    combineLatest([this.isCORValid$, this.isPCOValid$])
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(([isCORValid, isPCOValid]) => {
        if (
          !this.generalForm?.form?.controls?.number ||
          this.generalForm?.form?.controls?.number?.value === '' ||
          !this.model.type
        ) {
          return;
        }

        const isValid = this.model.type === 'Change Order Request' ? isCORValid : isPCOValid;

        if (isValid) {
          this.generalForm.form.controls.number.setErrors(null);
          return;
        }

        this.generalForm.form.controls.number.setErrors({ pattern: true });
      });
  }

  updatePco() {
    this.submitPcoUpdate.emit();
  }

  /**
   * Sets the status options based on the selected type
   * Clear already selected status
   * update COR or PCO number ID from backend
   */
  actionTypeChanged() {
    this.setStatusOptions();
    this.model.status = '';
    this.setNextNumber();
  }

  setStatusOptions() {
    if (this.model.type === 'Change Order Request') {
      this.statuses = changeOrderRequestStatuses;
      return;
    }
    this.statuses = POTENTIAL_CHANGE_ORDER_STATUSES;
  }

  protected filesChanged(files: ManagedFile[]) {
    this.model.files = [...files];
  }

  protected removeFile({ file, index }: { file: ManagedFile; index: number }) {
    if (isPersistedFile(file)) {
      this.model.files = this.model.files.map((modelFile) => {
        if (isPersistedFile(modelFile) && modelFile.id === file.id) {
          return {
            ...modelFile,
            toBeDeleted: true,
          };
        }
        return modelFile;
      });
    } else {
      this.model.files = this.model.files.filter((_, modelIndex) => modelIndex !== index);
    }
  }
}
